import {useAmbassadorshipEligibilityQuery} from "@/api/companyApi";
import CrownIcon from "@/assets/colorable/crown.svg?react";
import PendingIcon from "@/assets/colorable/pending.svg?react";
import Button from "@/components/Common/Button";
import clsxm from "@/lib/clsxm";
import useCampaignManagementStore from "@/store/campaignManagement";
import type {TAmbassadorshipRatingSubview} from "@/store/types";
import dayjs from "dayjs";
import {type FC} from "react";
import {useTranslation} from "react-i18next";

const HIGH_RATING_THRESHOLD = 4.5;

const AmbassadorshipSuggestion: FC<{
  className?: string;
  contentQuality: number;
  influencer: {user: {first_name?: string}; uuid?: number};
  onSelectSubview: (subview: TAmbassadorshipRatingSubview) => void;
  reliability: number;
}> = ({className, contentQuality, influencer, onSelectSubview, reliability}) => {
  const {t} = useTranslation(["influencer_invite", "common"]);
  const companyId = useCampaignManagementStore(state => state.campaign.company_profile?.id)!;

  const {data: eligibility} = useAmbassadorshipEligibilityQuery({companyId, influencerId: influencer.uuid!});

  const averageRating = reliability && contentQuality ? (reliability + contentQuality) / 2 : 0;

  const openAmbassadorActionSubview = (action: "accept" | "decline") =>
    onSelectSubview({
      data: {
        action,
        ambassador: null,
        companyId,
        entryPoint: "reporting",
        influencerId: influencer.uuid,
        isOpen: true,
        pendingAmbassador: eligibility?.pending_ambassador,
      },
      type: action,
    });

  if (eligibility?.blocked_reason === "request_pending") {
    return (
      <div className={clsxm("bg-primary-light-solid flex w-full items-start gap-2 rounded-md p-3", className)}>
        <PendingIcon className="text-primary-dark-solid mt-0.5 size-4 shrink-0" />
        <span className="text-neutral-black text-sm">
          {t("ambassador_invite_sent", {
            expiryDate: dayjs(eligibility.pending?.expires_at).format("D MMM"),
            sentDate: dayjs(eligibility.pending?.created).format("D MMM"),
          })}
        </span>
      </div>
    );
  }

  if (averageRating < HIGH_RATING_THRESHOLD) return null;

  if (eligibility?.blocked_reason === "application_pending") {
    return (
      <div className={clsxm("bg-primary-light-solid flex w-full flex-col items-start gap-3 rounded-md p-3", className)}>
        <div className="flex items-start gap-2">
          <CrownIcon className="text-primary-dark-solid mt-0.5 size-4 shrink-0" />
          <span className="text-neutral-black text-sm">
            {t("review_ambassador_application_description", {name: influencer.user.first_name})}
          </span>
        </div>
        <div className="flex gap-2">
          <Button
            buttonStyle="White"
            className="h-8 px-4 text-sm"
            onClick={() => openAmbassadorActionSubview("decline")}>
            {t("common:decline")}
          </Button>
          <Button
            buttonStyle="Gradient"
            className="h-8 px-4 text-sm"
            onClick={() => openAmbassadorActionSubview("accept")}>
            {t("common:accept")}
          </Button>
        </div>
      </div>
    );
  }

  if (!eligibility?.can_send) {
    return null;
  }

  return (
    <div className={clsxm("bg-primary-light-solid flex w-full flex-col items-start gap-3 rounded-md p-3", className)}>
      <div className="flex items-start gap-2">
        <CrownIcon className="text-primary-dark-solid mt-0.5 size-4 shrink-0" />
        <span className="text-neutral-black text-sm">
          {t("ambassador_rating_prompt", {name: influencer.user.first_name})}
        </span>
      </div>
      <Button
        buttonStyle="Gradient"
        className="h-8 px-4 text-sm"
        onClick={() => onSelectSubview({data: {companyId, entryPoint: "reporting", influencer}, type: "request"})}>
        {t("ask_them_to_be_your_ambassador")}
      </Button>
    </div>
  );
};

export default AmbassadorshipSuggestion;
